/*
 * Copyright (c) 2021. Dave van Rijn Development
 */
import './style/pdfText.scss';

import React, {CSSProperties, MouseEventHandler} from 'react';
import {
    CustomAppEvent,
    IndexedObject,
    PDFDisplay,
    PDFElementParams,
    PDFElementPersist,
    PDFElementType,
    PdfFont,
    PDFTextParams,
    PDFTextVariables
} from "../../util/interfaces";
import {PDFDraggable, PdfElement} from "../element/pdfElement";
import {debug, sanitizeHtml} from "../../util/miscUtil";
import {merge} from 'lodash';
import {PAGE_WIDTH, pxToPt, setPdfVariables, waitForFonts} from "../../util/pdfUtil";
import {directTimeout} from "../../util/componentUtil";
import {dispatchCustomEvent, showDialog} from "../../util/eventUtil";
import {remToPx, WithEvents} from "../../../../";

interface Props {
    onDelete: MouseEventHandler;
    onSaveHistory: VoidFunction;
    values: IndexedObject<string>;
    defaultSettings?: Partial<PDFTextParams>;
    id?: string;
    customVariables?: PDFTextVariables;
    defaultPosition: { x: number; y: number };
    draggable: boolean;
    onFocusElement: (element: PdfElement) => void;
    persistent?: PDFElementPersist;
    mainFont: PdfFont;
    linkedID?: string;
}

interface State {
    settings: PDFTextParams;
    dragging: boolean;
    focused: boolean;
}

export default class PdfText extends PdfElement<Props, State> {
    static defaultProps = {
        defaultPosition: {x: 0, y: 0},
    };
    private lastKnownCursor: number = 0;
    private field: HTMLTextAreaElement;
    private initialWidth: number | null = null;
    private currentText: string = '';
    private readonly events: CustomAppEvent[];

    constructor(props: Props) {
        super(props);
        this.state = {
            settings: merge({}, {
                text: 'Klik hier om deze tekst aan te passen.',
                bold: false,
                underline: false,
                italic: false,
                fontSize: remToPx(1),
                alignment: undefined,
                font: props.mainFont,
                color: '#000',
                persistent: PDFElementPersist.NOT_PERSISTENT,
                display: PDFDisplay.ALL_PAGES,
            }, props.defaultSettings),
            dragging: false,
            focused: false,
        }
        this.currentText = this.state.settings.text;
        this.events = [this.event, {eventName: 'onLinkedElementMoved', handler: this.onLinkedElementMoved}]
    }

    private onBlur = (evt: React.FocusEvent<HTMLTextAreaElement>) => {
        this.lastKnownCursor = evt.target.selectionEnd || 0;
        this.setState({focused: false}, this.setWidth);
        if (this.state.settings.text !== this.currentText) this.props.onSaveHistory();
    };

    private onFocus = (evt: React.FocusEvent) => {
        evt.stopPropagation();
        this.setState({focused: true}, this.setWidth);
        this.currentText = this.state.settings.text;
        this.onClick();
    };

    getTextValue = (): string => {
        const {settings, focused} = this.state, {values} = this.props;
        if (focused) return settings.text;
        return setPdfVariables(settings.text, values);
    }

    public setSetting = (key: string, value: any, callback?: VoidFunction, save: boolean = true) => {
        this.setState({settings: Object.assign({}, this.state.settings, {[key]: value})}, () => {
            this.setWidth();
            if (key !== 'text' && save)
                this.props.onSaveHistory();
            if (callback) callback();
        });
    };

    public onClickDelete = (evt: React.MouseEvent) => {
        const {persistent, onDelete} = this.props;
        if (persistent === 'confirm')
            showDialog('We raden aan om dit element wel te tonen op de factuur. Weet je zeker dat je dit ' +
                'element wilt verwijderen?', 'Element verwijderen',
                ['Nee', {label: 'Ja', onClick: onDelete}]);
        else onDelete(evt);
    };

    onClickEdit = (evt: React.MouseEvent) => {
        if (this.field) {
            this.field.focus();
            const {value} = this.field;
            this.field.selectionStart = value.length;
            this.field.selectionEnd = this.field.selectionStart;
        }
        this.onClick(evt);
    };

    onClick = (evt?: React.MouseEvent) => {
        if (evt)
            evt.stopPropagation();
        this.props.onFocusElement(this);
        if (this.draggable)
            this.draggable.setFocus();
    };

    onChange = (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
        const {target} = evt;
        this.setSetting('text', target.value, this.setWidth);
    }

    onClickBar = (evt: React.MouseEvent) => {
        this.onClick(evt);
    };

    onDrag = (position: { x: number; y: number }) => {
        const {linkedID} = this.props;
        if (linkedID)
            dispatchCustomEvent('onLinkedElementMoved', {position, id: linkedID, elementId: this.id});
    }

    onLinkedElementMoved = (data: { position: { x: number; y: number }, id: string; elementId: string }) => {
        const {linkedID} = this.props;
        if (this.draggable && linkedID && data.id === linkedID && data.elementId !== this.id)
            this.draggable.setPosition({x: data.position.x});
    };

    /**
     * Externally insert text add last know cursor position
     * @param newText
     */
    public insertText = (newText: string) => {
        const {settings} = this.state;
        let {text} = settings;
        text = text.substr(0, this.lastKnownCursor) + newText + text.substr(this.lastKnownCursor, text.length);
        this.setState({settings: Object.assign({}, settings, {text})}, () => {
            this.setWidth();
            if (this.field) {
                this.field.selectionStart = this.field.selectionStart + newText.length;
                this.field.selectionEnd = this.field.selectionStart;
                this.field.focus();
            }
            this.props.onSaveHistory();
        });
    };

    private setWidth = () => {
        const position = this.draggable?.getPosition() || {x: 0},
            maxWidth = PAGE_WIDTH - position.x, {linkedID} = this.props
        if (this.field) {
            this.field.style.width = '0px';
            this.field.style.width = this.field.scrollWidth + 'px';
            this.initialWidth = this.initialWidth ?? this.field.scrollWidth;
            if (this.field.scrollWidth > maxWidth && this.draggable) {
                this.draggable.setPosition({x: PAGE_WIDTH - this.field.scrollWidth}, () => {
                    dispatchCustomEvent('onLinkedElementMoved',
                        {position: this.draggable.getPosition(), id: linkedID, elementId: this.id});
                });
            }
        }
    };

    getParams = (convertPixels: boolean = true): PDFElementParams<PDFElementType.TEXT, PDFTextParams> => {
        const {settings} = this.state,
            {bold, italic, text, underline, alignment, font, color, persistent, display} = settings,
            fontSize = convertPixels ? pxToPt(settings.fontSize) : settings.fontSize, {linkedID} = this.props;
        return {
            dimensions: this.getDimensions(convertPixels),
            key: this.id,
            type: PDFElementType.TEXT,
            options: {fontSize: fontSize, bold, italic, text, underline, alignment, font, color, persistent, display},
            linkedID: linkedID,
        };
    }

    private getTextStyle = (): CSSProperties => {
        const {italic, underline, bold, fontSize, font, color} = this.state.settings,
            styles: CSSProperties = {fontSize, fontFamily: font, color};
        if (italic) styles.fontStyle = 'italic';
        if (underline) styles.textDecoration = 'underline';
        if (bold) styles.fontWeight = font === PdfFont.HELVETICA ? 600 : 500;
        return styles;
    };

    private getRows = () => {
        const {text} = this.state.settings;
        return text.split('\n').length;
    };

    componentDidMount = () => {
        directTimeout(this.setWidth);
        if (this.props.linkedID) {
            directTimeout(() => {
                if (this.draggable) {
                    debug('Dispatch event');
                    dispatchCustomEvent('onLinkedElementMoved',
                        {position: this.draggable.getPosition(), id: this.props.linkedID, elementId: this.id});
                }
            })
        }
        waitForFonts(this.setWidth);
    };

    componentDidUpdate = (prevProps: Props, prevState: State) => {
        const {settings} = this.state, prevSettings = prevState.settings;
        if (settings.font !== prevSettings.font) waitForFonts(this.setWidth);
    }

    render = () => {
        const {settings, dragging} = this.state, {
            draggable,
            defaultPosition,
            onSaveHistory,
            persistent
        } = this.props;
        return (
            <WithEvents events={this.events}>
                <PDFDraggable onDrag={this.onDrag} onDragEnd={this.onDragEnd} position={defaultPosition}
                              elementLabel='Tekst' alignment={settings.alignment} onDragStart={this.onDragStart}
                              draggable={draggable} dragging={dragging} resizeable={false} onDragged={onSaveHistory}
                              onResized={onSaveHistory} persistent={persistent} onDelete={this.onClickDelete}
                              onEdit={this.onClickEdit} ref={(ref: PDFDraggable) => {
                    this.draggable = ref
                }} forwardRef={(ref: HTMLDivElement) => {
                    this.element = ref
                }} onClickBar={this.onClickBar}>
                    <div className='pdf-text-container'>
                    <textarea className='pdf-text-input' style={this.getTextStyle()}
                              readOnly={settings.disabled === true}
                              value={sanitizeHtml(this.getTextValue())} onChange={this.onChange}
                              rows={this.getRows()} onFocus={this.onFocus} onBlur={this.onBlur} onClick={this.onClick}
                              ref={(ref: HTMLTextAreaElement) => {
                                  this.field = ref
                              }}/>
                    </div>
                </PDFDraggable>
            </WithEvents>
        )
    }
}